"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { useUser } from "@/contexts/user-provider";

import { toast } from "@/lib/use-toast";

import { updateNotificationSoundPreference } from "@/api/provider/admin/settings";

export type NotificationSoundOption =
  | "none"
  | "pop"
  | "droplet"
  | "gentle"
  | "aurora"
  | "cosmic";

// DEPRECATED: this localStorage key is being phased out in favour of
// `provider.notification_sound_preference` on the backend. We still read it
// once per device for the lazy migration so existing per-browser selections
// aren't lost, and we keep writing it so the brief window before `currentUser`
// resolves doesn't flicker to silence. Once all live users have a non-null
// backend value (track via a "rows with null preference" query), drop the
// localStorage code paths and the STORAGE_KEY entirely.
const STORAGE_KEY = "notification-sound-preference";

// Default sound for users who have never set a preference. Picked something
// audible rather than "none" so a brand-new account hears Whispers by default.
const DEFAULT_SOUND: NotificationSoundOption = "pop";

function isValidSound(
  value: string | null | undefined,
): value is NotificationSoundOption {
  return !!value && SOUND_OPTIONS.some((opt) => opt.value === value);
}

export const SOUND_OPTIONS: {
  value: NotificationSoundOption;
  label: string;
  description: string;
}[] = [
  { value: "none", label: "None", description: "No sound" },
  { value: "pop", label: "Pop", description: "Quick, satisfying pop" },
  { value: "droplet", label: "Droplet", description: "Soft water droplet" },
  { value: "gentle", label: "Gentle", description: "Soft melodic tone" },
  { value: "aurora", label: "Aurora", description: "Ethereal shimmer" },
  { value: "cosmic", label: "Cosmic", description: "Subtle space tone" },
];

// Sound generation using Web Audio API
function createAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  try {
    return new (window.AudioContext || (window as any).webkitAudioContext)();
  } catch {
    return null;
  }
}

// Pop sound - quick, satisfying bubble pop
function playPopSound(ctx: AudioContext) {
  const now = ctx.currentTime;

  // Main pop oscillator
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.frequency.setValueAtTime(600, now);
  osc.frequency.exponentialRampToValueAtTime(200, now + 0.08);
  osc.type = "sine";

  gain.gain.setValueAtTime(0.35, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);

  osc.start(now);
  osc.stop(now + 0.1);

  // Add a subtle click
  const click = ctx.createOscillator();
  const clickGain = ctx.createGain();
  click.connect(clickGain);
  clickGain.connect(ctx.destination);

  click.frequency.value = 1200;
  click.type = "triangle";
  clickGain.gain.setValueAtTime(0.15, now);
  clickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.02);

  click.start(now);
  click.stop(now + 0.02);
}

// Droplet sound - soft water drop
function playDropletSound(ctx: AudioContext) {
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);

  // Descending pitch like a water drop
  osc.frequency.setValueAtTime(1800, now);
  osc.frequency.exponentialRampToValueAtTime(400, now + 0.15);
  osc.type = "sine";

  filter.type = "lowpass";
  filter.frequency.setValueAtTime(2000, now);
  filter.Q.value = 2;

  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.25, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

  osc.start(now);
  osc.stop(now + 0.2);

  // Subtle resonance
  const res = ctx.createOscillator();
  const resGain = ctx.createGain();
  res.connect(resGain);
  resGain.connect(ctx.destination);

  res.frequency.setValueAtTime(600, now + 0.05);
  res.frequency.exponentialRampToValueAtTime(300, now + 0.2);
  res.type = "sine";
  resGain.gain.setValueAtTime(0.08, now + 0.05);
  resGain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

  res.start(now + 0.05);
  res.stop(now + 0.25);
}

// Gentle sound - soft melodic notification
function playGentleSound(ctx: AudioContext) {
  const now = ctx.currentTime;

  // Two-note gentle chime
  const notes = [523.25, 659.25]; // C5, E5
  const delays = [0, 0.12];

  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.frequency.value = freq;
    osc.type = "sine";

    filter.type = "lowpass";
    filter.frequency.value = 3000;

    const startTime = now + delays[i];
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(0.2, startTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.4);

    osc.start(startTime);
    osc.stop(startTime + 0.4);
  });
}

// Aurora sound - ethereal shimmer
function playAuroraSound(ctx: AudioContext) {
  const now = ctx.currentTime;

  // Layered ethereal tones
  const frequencies = [440, 554.37, 659.25]; // A4, C#5, E5 (A major chord)

  frequencies.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.frequency.value = freq;
    osc.type = "sine";

    filter.type = "lowpass";
    filter.frequency.value = 2000;
    filter.Q.value = 1;

    const delay = i * 0.03;
    const volume = 0.12 - i * 0.02;

    gain.gain.setValueAtTime(0, now + delay);
    gain.gain.linearRampToValueAtTime(volume, now + delay + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.5);

    osc.start(now + delay);
    osc.stop(now + delay + 0.5);
  });

  // Add shimmer with very high frequency
  const shimmer = ctx.createOscillator();
  const shimmerGain = ctx.createGain();
  shimmer.connect(shimmerGain);
  shimmerGain.connect(ctx.destination);

  shimmer.frequency.value = 2000;
  shimmer.type = "sine";
  shimmerGain.gain.setValueAtTime(0, now);
  shimmerGain.gain.linearRampToValueAtTime(0.03, now + 0.02);
  shimmerGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

  shimmer.start(now);
  shimmer.stop(now + 0.15);
}

// Cosmic sound - subtle space tone
function playCosmicSound(ctx: AudioContext) {
  const now = ctx.currentTime;

  // Deep base tone
  const base = ctx.createOscillator();
  const baseGain = ctx.createGain();
  base.connect(baseGain);
  baseGain.connect(ctx.destination);

  base.frequency.setValueAtTime(150, now);
  base.frequency.exponentialRampToValueAtTime(200, now + 0.3);
  base.type = "sine";

  baseGain.gain.setValueAtTime(0, now);
  baseGain.gain.linearRampToValueAtTime(0.15, now + 0.05);
  baseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

  base.start(now);
  base.stop(now + 0.4);

  // High harmonic sweep
  const high = ctx.createOscillator();
  const highGain = ctx.createGain();
  const highFilter = ctx.createBiquadFilter();

  high.connect(highFilter);
  highFilter.connect(highGain);
  highGain.connect(ctx.destination);

  high.frequency.setValueAtTime(800, now);
  high.frequency.exponentialRampToValueAtTime(1200, now + 0.2);
  high.type = "triangle";

  highFilter.type = "bandpass";
  highFilter.frequency.value = 1000;
  highFilter.Q.value = 5;

  highGain.gain.setValueAtTime(0, now);
  highGain.gain.linearRampToValueAtTime(0.08, now + 0.03);
  highGain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

  high.start(now);
  high.stop(now + 0.25);

  // Subtle reverb-like tail
  const tail = ctx.createOscillator();
  const tailGain = ctx.createGain();
  tail.connect(tailGain);
  tailGain.connect(ctx.destination);

  tail.frequency.value = 400;
  tail.type = "sine";

  tailGain.gain.setValueAtTime(0, now + 0.1);
  tailGain.gain.linearRampToValueAtTime(0.05, now + 0.15);
  tailGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

  tail.start(now + 0.1);
  tail.stop(now + 0.5);
}

// Send confirmation - short ascending "whoosh" (distinct from receive sounds)
function playSendTone(ctx: AudioContext) {
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);

  // Quick upward sweep - like a message flying off
  osc.frequency.setValueAtTime(400, now);
  osc.frequency.exponentialRampToValueAtTime(1200, now + 0.06);
  osc.type = "sine";

  filter.type = "lowpass";
  filter.frequency.value = 2500;

  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.2, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

  osc.start(now);
  osc.stop(now + 0.08);
}

const SOUND_PLAYERS: Record<
  Exclude<NotificationSoundOption, "none">,
  (ctx: AudioContext) => void
> = {
  pop: playPopSound,
  droplet: playDropletSound,
  gentle: playGentleSound,
  aurora: playAuroraSound,
  cosmic: playCosmicSound,
};

type AudioState = "running" | "suspended" | "unsupported";

// Singletons so every `useNotificationSound` consumer shares one AudioContext
// and one audio-state value. Without this, each mount creates its own context
// and resuming one (e.g. clicking the bell-panel "Enable sound" button) leaves
// other instances' contexts suspended — the settings-page banner would never
// hide. `useSyncExternalStore` keeps every subscriber in lockstep.
let sharedAudioContext: AudioContext | null = null;
let sharedAudioState: AudioState = "suspended";
const audioStateListeners = new Set<() => void>();
let sharedAudioInitialized = false;

function notifyAudioStateListeners() {
  audioStateListeners.forEach((cb) => cb());
}

function setSharedAudioState(next: AudioState) {
  if (sharedAudioState === next) return;
  sharedAudioState = next;
  notifyAudioStateListeners();
}

function initSharedAudio() {
  if (sharedAudioInitialized || typeof window === "undefined") return;
  sharedAudioInitialized = true;
  const ctx = createAudioContext();
  if (!ctx) {
    setSharedAudioState("unsupported");
    return;
  }
  sharedAudioContext = ctx;
  setSharedAudioState(ctx.state === "running" ? "running" : "suspended");
  ctx.addEventListener("statechange", () => {
    setSharedAudioState(ctx.state === "running" ? "running" : "suspended");
  });
}

function getSharedAudioContext(): AudioContext | null {
  initSharedAudio();
  if (sharedAudioContext?.state === "suspended") {
    // resume() is async but we don't need to await — the statechange listener
    // will broadcast the new state to all subscribers when the browser flips it.
    sharedAudioContext.resume();
  }
  return sharedAudioContext;
}

function subscribeAudioState(cb: () => void) {
  audioStateListeners.add(cb);
  return () => {
    audioStateListeners.delete(cb);
  };
}

function getAudioStateSnapshot(): AudioState {
  return sharedAudioState;
}

// SSR snapshot — we have no AudioContext on the server, so treat as suspended
// (matches the "needs a user gesture" client default).
function getServerAudioStateSnapshot(): AudioState {
  return "suspended";
}

export function useNotificationSound() {
  const { currentUser, setCurrentUser } = useUser();
  const [soundPreference, setSoundPreference] =
    useState<NotificationSoundOption>(DEFAULT_SOUND);
  const [isLoaded, setIsLoaded] = useState(false);
  const audioState = useSyncExternalStore(
    subscribeAudioState,
    getAudioStateSnapshot,
    getServerAudioStateSnapshot,
  );
  // Track which user we've reconciled for, not just "have we reconciled" —
  // otherwise a logout/login as a different user in the same tab would skip
  // re-sync and the previous user's sound would leak through.
  const seededUserIdRef = useRef<string | null>(null);
  const backendValue = currentUser?.notificationSoundPreference;

  // Seed synchronously from localStorage so the user's existing sound plays
  // immediately on mount, even before `currentUser` resolves.
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isValidSound(stored)) setSoundPreference(stored);
    setIsLoaded(true);
  }, []);

  // Once the user loads, reconcile with the backend. If the backend has a
  // value, it wins. If not, we lazily migrate the localStorage value up so the
  // preference stops being scoped to this browser.
  useEffect(() => {
    if (!currentUser || seededUserIdRef.current === currentUser.id) return;
    seededUserIdRef.current = currentUser.id;

    if (isValidSound(backendValue)) {
      setSoundPreference(backendValue);
      localStorage.setItem(STORAGE_KEY, backendValue);
      return;
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    if (isValidSound(stored)) {
      // Capture which user we sent the request for; if a different user is
      // logged in by the time it returns, don't patch their state.
      const requestUserId = currentUser.id;
      updateNotificationSoundPreference(stored).then((ok) => {
        if (!ok) return;
        setCurrentUser((prev) =>
          prev && prev.id === requestUserId
            ? { ...prev, notificationSoundPreference: stored }
            : prev,
        );
      });
    }
  }, [currentUser, backendValue, setCurrentUser]);

  // Ensure the shared context exists so audioState reflects reality on first
  // render in a freshly-mounted tree (the SSR snapshot returns "suspended").
  useEffect(() => {
    initSharedAudio();
  }, []);

  // Save preference to localStorage AND the backend so it follows the user across devices.
  // localStorage still wins on initial paint to avoid a network round-trip flicker.
  const updateSoundPreference = useCallback(
    (value: NotificationSoundOption) => {
      setSoundPreference(value);
      localStorage.setItem(STORAGE_KEY, value);
      const requestUserId = currentUser?.id;
      updateNotificationSoundPreference(value).then((ok) => {
        if (!ok) {
          // Local state already reflects the new choice — the next reload from
          // a different device will revert, but the user sees the failure and
          // can retry by picking again.
          toast({
            title: "Couldn't save sound preference",
            description:
              "Your sound choice didn't sync to your account. It will still apply on this device.",
            variant: "error",
          });
          return;
        }
        setCurrentUser((prev) =>
          prev && prev.id === requestUserId
            ? { ...prev, notificationSoundPreference: value }
            : prev,
        );
      });
    },
    [currentUser?.id, setCurrentUser],
  );

  // Play the notification sound (for incoming/receive)
  const playNotificationSound = useCallback(() => {
    if (soundPreference === "none") {
      return;
    }

    const audioContext = getSharedAudioContext();
    if (!audioContext) return;

    try {
      SOUND_PLAYERS[soundPreference](audioContext);
    } catch (error) {
      console.error("Error playing notification sound:", error);
    }
  }, [soundPreference]);

  // Play unique send confirmation sound (always same tone, distinct from receive)
  // Always plays regardless of notification preference — it's user-initiated feedback
  const playSendSound = useCallback(() => {
    const audioContext = getSharedAudioContext();
    if (!audioContext) return;

    try {
      playSendTone(audioContext);
    } catch (error) {
      console.error("Error playing send sound:", error);
    }
  }, []);

  // Preview a specific sound (for settings UI)
  const previewSound = useCallback((soundType: NotificationSoundOption) => {
    if (soundType === "none") {
      return;
    }

    const audioContext = getSharedAudioContext();
    if (!audioContext) return;

    try {
      SOUND_PLAYERS[soundType](audioContext);
    } catch (error) {
      console.error("Error playing preview sound:", error);
    }
  }, []);

  return {
    soundPreference,
    updateSoundPreference,
    playNotificationSound,
    playSendSound,
    previewSound,
    isLoaded,
    // True only when the browser is actually willing to play audio right now.
    // False covers both the autoplay-policy "needs a user interaction first"
    // case and the rare "AudioContext unsupported" case.
    canPlaySound: audioState === "running",
    audioState,
  };
}
